import { createContext, useContext, ReactNode, Dispatch, SetStateAction } from "react";
import { useSessionStorage } from "../hooks/useSessionStorage";
import { ReferenciaSelecionadaPayload } from "../types/resources.types";
import { Surgery } from "../types/patient.types";
import { SurgeryRequest } from "../types/pdf.types";
import { Company } from "../types/company.types";

// O pedido em edição, de "Novo pedido" até o PDF. Tudo vai para o
// sessionStorage: sobrevive ao refresh da aba, não a outra aba.
// ponytail: string[][] / [string, string][] ficam até cada tela migrar para
// ProcedureItem / OpmeItem (src/types/order.types.ts) na Fase 5.

type ProcedureRow = { code: string; description: string; quantity: string };

const EMPTY_SURGERY_REQUEST: SurgeryRequest = {
  patientName: "",
  patientDescription: "",
  opmeSummary: "",
  procedures: "",
  cids: "",
  issueDate: "",
  desiredDateTime: "",
  hospital: "",
};

// Campo do rascunho: guarda o valor inicial para o resetDraft() voltar a ele.
function useDraftField<T>(resets: Array<() => void>, key: string, initial: T): [T, Dispatch<SetStateAction<T>>] {
  const field = useSessionStorage<T>(key, initial);
  resets.push(() => field[1](initial));
  return field;
}

function useOrderDraftState() {
  const resets: Array<() => void> = [];
  // Identificador do pedido no backend (/chat/save).
  const [timestamp, setTimestamp] = useDraftField(resets, "timestamp", "");
  // Pedido salvo carregado de "Pedidos salvos".
  const [surgerie, setSurgerie] = useDraftField<Surgery | undefined>(resets, "surgerie", undefined);

  // Paciente
  const [patientName, setPatientName] = useDraftField(resets, "patientName", "");
  const [birthDate, setBirthDate] = useDraftField(resets, "birthDate", "");
  const [cpf, setCpf] = useDraftField(resets, "cpf", "");
  const [sexo, setSexo] = useDraftField(resets, "sexo", "");
  const [documentNumber, setDocumentNumber] = useDraftField(resets, "documentNumber", "");
  const [convenio, setConvenio] = useDraftField(resets, "convenio", "");
  const [documentValidity, setDocumentValidity] = useDraftField(resets, "documentValidity", "");

  // Histórico e laudos
  const [newInputRequest, setnewInputRequest] = useDraftField(resets, "newInputRequest", "");
  const [laudos, setLaudos] = useDraftField<string[]>(resets, "laudos", [""]);
  // Separador que o backend sempre recebeu entre os laudos.
  const laudoText = laudos.join('". "');

  // Procedimentos
  const [checkboxItems, setCheckboxItems] = useDraftField<string[][]>(resets, "checkboxItems", []);
  const [initialCheckboxItems, setInitialCheckboxItems] = useDraftField<string[][]>(resets, "initialCheckboxItems", []);
  const [savedCheckboxItems, setSavedCheckboxItems] = useDraftField<string[][]>(resets, "savedCheckboxItems", []);
  const [checkedItems, setCheckedItems] = useDraftField<ProcedureRow[]>(resets, "checkedItems", []);
  const [checkedKit, setCheckedKit] = useDraftField<ProcedureRow[]>(resets, "checkedKit", []);
  const [selectedButtonCheckKits, setSelectedButtonCheckKits] = useDraftField(resets, "selectedButtonCheckKits", "pacotes");
  const [selectedCheckKitsType, setSelectedCheckKitsType] = useDraftField(resets, "selectedCheckKitsType", "Pacotes Personalizados");
  const [selectedProcedureKitIndex, setSelectedProcedureKitIndex] = useDraftField(resets, "selectedProcedureKitIndex", -1);

  // OPME (sempre escolha manual do médico)
  const [opmeItems, setOpmeItems] = useDraftField<Array<[string, string]>>(resets, "opmeItems", []);
  const [opmeKit, setOpmeKit] = useDraftField<Array<[string, string]>>(resets, "opmeKit", []);
  const [selectedItems, setSelectedItems] = useDraftField<{ item: string; quantity: number }[]>(resets, "selectedItems", []);
  const [resumoOPME, setResumoOPME] = useDraftField(resets, "resumoOPME", "");
  const [selectedButtonOpmeKits, setSelectedButtonOpmeKits] = useDraftField(resets, "selectedButtonOpmeKits", "pacotes");
  const [selectedOpmeKitsType, setSelectedOpmeKitsType] = useDraftField(resets, "selectedOpmeKitsType", "Pacotes Personalizados");
  const [selectedOpmeKitIndex, setSelectedOpmeKitIndex] = useDraftField(resets, "selectedOpmeKitIndex", -1);
  const [selectedCompanies, setSelectedCompanies] = useDraftField<Company[]>(resets, "selectedCompanies", []);
  // Pacote que a IA recomendou, por lista e caso (useKitRecommendation): voltar à aba não gasta IA de novo.
  const [kitRecommendations, setKitRecommendations] = useDraftField<Record<string, number>>(resets, "kitRecommendations", {});

  // Indicação cirúrgica
  const [surgery, setSurgery] = useDraftField(resets, "surgery", "");
  const [hospital, setHospital] = useDraftField(resets, "hospital", "");
  const [desiredDate, setDesiredDate] = useDraftField(resets, "desiredDate", "");
  const [observacoes, setObservacoes] = useDraftField(resets, "observacoes", "");

  // Pedido gerado
  const [EditableResponse, setEditableResponse] = useDraftField(resets, "EditableResponse", "");
  const [surgeryRequestData, setSurgeryRequestData] = useDraftField<SurgeryRequest>(resets, "surgeryRequestData", EMPTY_SURGERY_REQUEST);
  const [lastApprovedRefs, setLastApprovedRefs] = useDraftField<ReferenciaSelecionadaPayload[] | undefined>(resets, "lastApprovedRefs", undefined);
  // Descrição resumida da guia de internação já confirmada, com a completa da
  // qual foi derivada (DownloadPage ↔ PatientDataPage, sem refazer a IA).
  // Fica inválida sozinha quando `completa` deixa de bater com o pedido atual.
  const [guiaResumidaCache, setGuiaResumidaCache] = useDraftField<{ resumida: string; completa: string } | null>(resets, "guiaResumidaCache", null);

  // Entradas (paciente, caso, laudos, cirurgia, procedimentos) de onde saiu o EditableResponse.
  // Igual às atuais = o pedido já gerado vale; a Revisão não chama a IA de novo.
  const [orderInputsKey, setOrderInputsKey] = useDraftField(resets, "orderInputsKey", "");

  // O médico editou um pedido salvo? Decidem se a IA é chamada de novo.
  const [modificadoHistorico, setModificadoHistorico] = useDraftField(resets, "modificadoHistorico", false);
  const [modificadoLaudo, setModificadoLaudo] = useDraftField(resets, "modificadoLaudo", false);
  const [modificadoProcedimentos, setModificadoProcedimentos] = useDraftField(resets, "modificadoProcedimentos", false);
  const [modificadoOpme, setModificadoOpme] = useDraftField(resets, "modificadoOpme", false);
  const [modificadoTipoCirurgia, setModificadoTipoCirurgia] = useDraftField(resets, "modificadoTipoCirurgia", false);

  return {
    timestamp, setTimestamp, surgerie, setSurgerie,
    patientName, setPatientName, birthDate, setBirthDate, cpf, setCpf, sexo, setSexo,
    documentNumber, setDocumentNumber, convenio, setConvenio, documentValidity, setDocumentValidity,
    newInputRequest, setnewInputRequest, laudoText, laudos, setLaudos,
    checkboxItems, setCheckboxItems,
    initialCheckboxItems, setInitialCheckboxItems, savedCheckboxItems, setSavedCheckboxItems,
    checkedItems, setCheckedItems, checkedKit, setCheckedKit,
    selectedButtonCheckKits, setSelectedButtonCheckKits, selectedCheckKitsType, setSelectedCheckKitsType,
    selectedProcedureKitIndex, setSelectedProcedureKitIndex,
    opmeItems, setOpmeItems, opmeKit, setOpmeKit, selectedItems, setSelectedItems, resumoOPME, setResumoOPME,
    selectedButtonOpmeKits, setSelectedButtonOpmeKits, selectedOpmeKitsType, setSelectedOpmeKitsType,
    selectedOpmeKitIndex, setSelectedOpmeKitIndex, selectedCompanies, setSelectedCompanies,
    kitRecommendations, setKitRecommendations,
    surgery, setSurgery, hospital, setHospital, desiredDate, setDesiredDate, observacoes, setObservacoes,
    EditableResponse, setEditableResponse, surgeryRequestData, setSurgeryRequestData,
    lastApprovedRefs, setLastApprovedRefs, guiaResumidaCache, setGuiaResumidaCache,
    modificadoHistorico, setModificadoHistorico, modificadoLaudo, setModificadoLaudo,
    modificadoProcedimentos, setModificadoProcedimentos, modificadoOpme, setModificadoOpme,
    modificadoTipoCirurgia, setModificadoTipoCirurgia,
    orderInputsKey, setOrderInputsKey,
    // Volta todo o rascunho ao início: pedido novo não herda nada do anterior.
    resetDraft: () => resets.forEach((reset) => reset()),
  };
}

export type OrderDraft = ReturnType<typeof useOrderDraftState>;

const OrderDraftContext = createContext<OrderDraft | undefined>(undefined);

export const OrderDraftProvider = ({ children }: { children: ReactNode }) => (
  <OrderDraftContext.Provider value={useOrderDraftState()}>{children}</OrderDraftContext.Provider>
);

export const useOrderDraft = () => {
  const context = useContext(OrderDraftContext);
  if (!context) throw new Error("useOrderDraft deve ser usado dentro de um OrderDraftProvider");
  return context;
};
